import React from 'react';
import { motion } from 'framer-motion';
import { 
  Download, 
  RefreshCw, 
  CheckCircle, 
  AlertCircle, 
  Clock,
  Star,
  Zap,
  Eye
} from 'lucide-react';
import { useChartImport } from '../hooks/useChartImport';
import { useUserStore } from '../stores/userStore';

interface AstrologicalChartDisplayProps {
  chartData?: any;
}

const AstrologicalChartDisplay: React.FC<AstrologicalChartDisplayProps> = ({ chartData }) => {
  const { user } = useUserStore();
  const {
    isImporting,
    isComplete,
    charts,
    error,
    hasCompleteBirthData,
    triggerImport,
    // resetImport
  } = useChartImport();

  const getChartSourceIcon = (source: string) => {
    switch (source.toLowerCase()) {
      case 'internal_astrology_service':
        return <Star className="w-4 h-4 text-yellow-500" />;
      case 'astroapi':
        return <Zap className="w-4 h-4 text-blue-500" />;
      case 'swiss ephemeris':
        return <Eye className="w-4 h-4 text-green-500" />;
      default:
        return <Star className="w-4 h-4 text-gray-500" />;
    }
  };

  const getChartSourceName = (source: string) => {
    switch (source.toLowerCase()) {
      case 'internal_astrology_service':
        return 'RRAASI Analysis';
      case 'astroapi':
        return 'AstroAPI';
      case 'swiss ephemeris':
        return 'Swiss Ephemeris';
      default:
        return source;
    }
  };

  if (!user) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">Please log in to view your astrological charts</p>
      </div>
    );
  }

  if (!hasCompleteBirthData) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-8"
      >
        <AlertCircle className="w-12 h-12 text-orange-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          Complete Birth Data Required
        </h3>
        <p className="text-gray-600 mb-4">
          To automatically import your astrological charts, please provide your complete birth information including:
        </p>
        <ul className="text-sm text-gray-500 space-y-1 mb-6">
          <li>• Birth date (day, month, year)</li>
          <li>• Birth time (hour, minute)</li>
          <li>• Birth location (latitude, longitude)</li>
        </ul>
        <button
          onClick={() => window.location.href = '/profile'}
          className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
        >
          Update Profile
        </button>
      </motion.div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Your Astrological Charts</h2>
          <p className="text-gray-600">Automatically imported from multiple sources</p>
        </div>
        
        <div className="flex items-center space-x-2">
          {isImporting && (
            <div className="flex items-center space-x-2 text-blue-600">
              <RefreshCw className="w-4 h-4 animate-spin" />
              <span className="text-sm">Importing...</span>
            </div>
          )}
          
          {isComplete && (
            <div className="flex items-center space-x-2 text-green-600">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">Complete</span>
            </div>
          )}
          
          <button
            onClick={triggerImport}
            disabled={isImporting}
            className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${isImporting ? 'animate-spin' : ''}`} />
            <span>Refresh Charts</span>
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-lg p-4"
        >
          <div className="flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
          </div>
        </motion.div>
      )}

      {/* Charts Grid */}
      {charts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {charts.map((chart, index) => (
            <motion.div
              key={chart.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-all duration-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  {getChartSourceIcon(chart.source)}
                  <span className="font-semibold text-gray-800">
                    {getChartSourceName(chart.source)}
                  </span>
                </div>
                {chart.hasImage && (
                  <Download className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer" />
                )}
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>
                    Imported {new Date(chart.importedAt).toLocaleDateString()}
                  </span>
                </div>
                
                <div className="text-xs text-gray-500">
                  Chart ID: {chart.id.slice(0, 8)}...
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <div className="bg-gray-50 rounded-lg p-8">
            <Star className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              No Charts Imported Yet
            </h3>
            <p className="text-gray-600 mb-6">
              Click the button below to automatically import your astrological charts from multiple sources.
            </p>
            <button
              onClick={triggerImport}
              disabled={isImporting}
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 mx-auto"
            >
              <Zap className="w-4 h-4" />
              <span>Import Charts</span>
            </button>
          </div>
        </motion.div>
      )}

      {/* Astro Profile Display */}
      {user.astroProfile && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border border-purple-200"
        >
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Your Astrological Profile
          </h3>
          
          {user.astroProfile.personalityTraits && (
            <div className="mb-4">
              <h4 className="font-medium text-gray-700 mb-2">Personality Traits</h4>
              <div className="flex flex-wrap gap-2">
                {user.astroProfile.personalityTraits.slice(0, 6).map((trait: string, index: number) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                  >
                    {trait}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {user.astroProfile.recommendations && (
            <div>
              <h4 className="font-medium text-gray-700 mb-2">Recommendations</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                {user.astroProfile.recommendations.slice(0, 3).map((rec: string, index: number) => (
                  <li key={index} className="flex items-start space-x-2">
                    <span className="text-purple-500 mt-1">•</span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default AstrologicalChartDisplay; 